// Organization types for TypeScript

export type SubscriptionTier = 'team' | 'club' | 'academy' | 'enterprise';
export type SubscriptionStatus = 'trial' | 'active' | 'expired' | 'cancelled';
export type MemberRole = 'owner' | 'admin';

export interface Organization {
  id: string;
  name: string;
  subscription_tier: SubscriptionTier;
  subscription_status: SubscriptionStatus;
  subscription_expires_at?: string;
  owner_id: string;
  auto_renewal: boolean;
  /** Карта для автопродления сохранена (без неё автопродление не списывает). */
  payment_method_saved?: boolean;
  /** Клуб уже получал пробный период (он один раз на клуб). */
  trial_used?: boolean;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  is_read_only: boolean;
}

export interface OrganizationStats {
  members: number;
  athletes: number;
  assistants: number;
  groups: number;
  coaches: number;
  limits: {
    max_members: number;
    max_athletes: number;
    max_assistants: number;
    max_groups: number;
    max_coaches: number;
    max_ai_requests: number;
    tier_name: string;
    tier_price: number;
  };
}

export interface Member {
  id: string;
  organization_id: string;
  user_id: string;
  user_name?: string; // Optional: may not be loaded in all contexts
  role: MemberRole;
  invited_by: string;
  invited_at: string;
  joined_at: string;
}

export interface Invite {
  id: string;
  organization_id: string;
  code: string;
  role: MemberRole;
  created_by: string;
  created_at: string;
  expires_at: string;
  used_by?: string;
  used_at?: string;
  max_uses: number;
  current_uses: number;
  remaining_uses: number;
  revoked_at?: string;
  revoked_by?: string;
  is_active: boolean;
  is_expired: boolean;
  is_revoked: boolean;
}

/** Новый клуб всегда на FREE: тариф сервер не принимает (поле tier он игнорирует). */
export interface CreateOrganizationRequest {
  user_id: string;
  name: string;
}

export interface GenerateInviteRequest {
  role: MemberRole;
  valid_days?: number;  // Optional: defaults to 7 days on backend
  max_uses?: number;    // Optional: defaults to 10 on backend
}

export interface UseInviteRequest {
  code: string;
}

export interface TransferOwnershipRequest {
  new_owner_id: string;
}

// Context types for switching between Personal and Organization
export type ContextType = 'personal' | 'organization';

export interface AppContext {
  type: ContextType;
  organization_id?: string;
}

// Tier information for UI
export const TIER_INFO: Record<SubscriptionTier, {
  name: string;
  price: number;
  priceYearly: number;
  members: number;
  athletes: number;
  assistants: number;
  groups: number;
  coaches: number;
  aiRequests: number;
  features: string[];
}> = {
  team: {
    name: 'TEAM',
    price: 1990,
    priceYearly: 19900,
    members: 3,
    athletes: 100,
    assistants: 10,
    groups: 20,
    coaches: 10,
    aiRequests: 250,
    features: [
      'До 3 членов команды',
      'До 100 спортсменов',
      'До 10 ассистентов',
      'До 20 групп',
      'До 10 тренеров',
      '250 AI-запросов/мес'
    ]
  },
  club: {
    name: 'CLUB',
    price: 4990,
    priceYearly: 49900,
    members: 10,
    athletes: 500,
    assistants: 30,
    groups: 50,
    coaches: 30,
    aiRequests: 1000,
    features: [
      'До 10 членов команды',
      'До 500 спортсменов',
      'До 30 ассистентов',
      'До 50 групп',
      'До 30 тренеров',
      '1000 AI-запросов/мес'
    ]
  },
  academy: {
    name: 'ACADEMY',
    price: 9990,
    priceYearly: 99900,
    members: 30,
    athletes: 2000,
    assistants: 100,
    groups: 200,
    coaches: 100,
    aiRequests: 5000,
    features: [
      'До 30 членов команды',
      'До 2000 спортсменов',
      'До 100 ассистентов',
      'До 200 групп',
      'До 100 тренеров',
      '5000 AI-запросов/мес'
    ]
  },
  enterprise: {
    name: 'ENTERPRISE',
    price: 0,
    priceYearly: 0,
    members: -1,
    athletes: -1,
    assistants: -1,
    groups: -1,
    coaches: -1,
    aiRequests: -1,
    features: [
      'Неограниченно членов',
      'Неограниченно спортсменов',
      'Неограниченно ассистентов',
      'Неограниченно групп',
      'Неограниченно тренеров',
      'Неограниченно AI-запросов',
      'Индивидуальные условия'
    ]
  }
};

// Helper function to format tier limits
export function formatLimit(value: number): string {
  return value === -1 ? '∞' : value.toString();
}

// Helper function to get tier color
export function getTierColor(tier: SubscriptionTier): string {
  switch (tier) {
    case 'team':
      return '#3b82f6'; // blue
    case 'club':
      return '#8b5cf6'; // purple
    case 'academy':
      return '#f59e0b'; // amber
    case 'enterprise':
      return '#10b981'; // emerald
    default:
      return '#6b7280'; // gray
  }
}

// Helper function to get status color
export function getStatusColor(status: SubscriptionStatus): string {
  switch (status) {
    case 'trial':
      return '#3b82f6'; // blue
    case 'active':
      return '#10b981'; // green
    case 'expired':
      return '#ef4444'; // red
    case 'cancelled':
      return '#6b7280'; // gray
    default:
      return '#6b7280';
  }
}

// Helper function to format status text
export function formatStatus(status: SubscriptionStatus): string {
  switch (status) {
    case 'trial':
      return 'Триал';
    case 'active':
      return 'Активна';
    case 'expired':
      return 'Истекла';
    case 'cancelled':
      return 'Отменена';
    default:
      return status;
  }
}
